import React, { useState, useEffect, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import ApiService from '../../data/services/ApiService'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { updateCompany } from '../../store/actions'
import { Form, FormGroup, Label, Input, Button, Row, Col, Table, Nav, NavItem, NavLink } from 'reactstrap'

const hostname = window && window.location && window.location.hostname
let stripePromise
switch (hostname) {
    case 'localhost':
        stripePromise = loadStripe('pk_test_AmmH2yRbUj2dudN847bdsEoG00STbHhfct')
        break
    default:
        stripePromise = loadStripe('pk_live_Qwwzo7PC35kHsv30h2Zo1Z3q00Ql6KxNty')
        break
}

const PAYMENT_METHODS_ALL = [
    { id: 'card', label: 'Tarjeta' },
    { id: 'spei', label: 'SPEI (Transferencia)' },
    { id: 'oxxo', label: 'OXXO' }
]

const PAYMENT_METHODS_CARD_ONLY = [
    { id: 'card', label: 'Tarjeta' }
]

const POLL_INTERVAL_MS = 3000
const POLL_MAX_ATTEMPTS = 20

const isPaidPlan = (plan) => {
    const planName = String(plan?.nombre || '').trim().toLowerCase()
    return Boolean(
        plan &&
        plan.status === true &&
        planName !== 'libre' &&
        planName !== 'trial' &&
        Number(plan.precio) > 0 &&
        Number(plan.timbres) > 0
    )
}

const choosePlan = (plans, preferredPlan) => {
    const paidPlans = (plans || []).filter(isPaidPlan)
    if (isPaidPlan(preferredPlan)) {
        return paidPlans.find(plan => plan._id === preferredPlan._id) || preferredPlan
    }

    return paidPlans.find(plan => plan.nombre === 'arranque') || paidPlans[0] || null
}

const PaymentForm = (props) => {
    // 'subscription' (default) or 'credits'
    const purchaseType = props.purchaseType || 'subscription'
    const PAYMENT_METHODS = purchaseType === 'credits' ? PAYMENT_METHODS_ALL : PAYMENT_METHODS_CARD_ONLY
    const [plans, setPlans] = useState([])
    const [selectedPlan, setSelectedPlan] = useState(null)
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null)
    const [useExistingPayment, setUseExistingPayment] = useState(false)
    const [loading, setLoading] = useState(false)
    const [paymentMethod, setPaymentMethod] = useState('card')
    const [pendingInstructions, setPendingInstructions] = useState(null)
    const [polling, setPolling] = useState(false)
    const stripe = useStripe()
    const elements = useElements()

    // Determine if this is a plan-change vs first subscription
    const alreadySubscribed = props.empresa && props.empresa.pagante && props.empresa.status

    useEffect(() => {
        const nextPlans = props.plans || []
        setPlans(nextPlans)
        setSelectedPlan(choosePlan(nextPlans, props.currentPlan))
        fetchDefaultPaymentMethod()
    }, [])

    useEffect(() => {
        if (props.plans) {
            setPlans(props.plans)
            setSelectedPlan(choosePlan(props.plans, props.currentPlan))
        }
    }, [props.plans, props.currentPlan])

    const fetchDefaultPaymentMethod = async () => {
        try {
            const response = await ApiService.fetchDefaultPaymentMethod()
            setDefaultPaymentMethod(response.data.paymentMethod || null)
        } catch (_) {}
    }

    // Poll subscription status until active or max attempts reached
    const pollUntilActive = useCallback((onSuccess) => {
        setPolling(true)
        let attempts = 0
        const interval = setInterval(async () => {
            attempts += 1
            try {
                const res = await ApiService.getSubscriptionStatus()
                if (res.data?.data?.active) {
                    clearInterval(interval)
                    setPolling(false)
                    onSuccess()
                }
            } catch (_) {}
            if (attempts >= POLL_MAX_ATTEMPTS) {
                clearInterval(interval)
                setPolling(false)
                toastr.warning('Tu pago está siendo procesado. Recibirás un correo de confirmación cuando se confirme.')
            }
        }, POLL_INTERVAL_MS)
    }, [])

    const handleCardSubmit = async (event) => {
        event.preventDefault()
        if (!stripe || !elements || !selectedPlan) { return }
        setLoading(true)

        if (window.analytics) {
            window.analytics.track('Checkout iniciado', {
                planId: selectedPlan._id,
                planName: selectedPlan.nombre,
                purchaseType
            })
        }

        try {
            let paymentMethodId = null

            if (useExistingPayment && defaultPaymentMethod) {
                paymentMethodId = defaultPaymentMethod.id
            } else {
                const cardElement = elements.getElement(CardElement)
                const { error, paymentMethod: pm } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement
                })
                if (error) {
                    toastr.error(error.message)
                    setLoading(false)
                    return
                }
                paymentMethodId = pm.id
            }

            if (alreadySubscribed) {
                // Plan change
                const response = await ApiService.cambiarPlan({ planId: selectedPlan._id })
                if (response.data.success) {
                    if (window.analytics) {
                        window.analytics.track('Checkout completado', {
                            planId: selectedPlan._id,
                            planName: selectedPlan.nombre,
                            purchaseType
                        })
                    }
                    toastr.success(response.data.message)
                    try {
                        const profileRes = await ApiService.getProfile()
                        if (profileRes.data?.profile?.empresa) { props.updateCompany(profileRes.data.profile.empresa) }
                    } catch (_) {}
                    if (props.onSuccess) { props.onSuccess() }
                } else {
                    toastr.error(response.data.message || 'No pude cambiar el plan')
                }
            } else {
                const response = await ApiService.createSubscription({ paymentMethod: paymentMethodId, planId: selectedPlan._id })
                if (response.data.success) {
                    if (window.analytics) {
                        window.analytics.track('Checkout completado', {
                            planId: selectedPlan._id,
                            planName: selectedPlan.nombre,
                            purchaseType
                        })
                    }
                    toastr.success(response.data.message)
                    try {
                        const profileRes = await ApiService.getProfile()
                        if (profileRes.data?.profile?.empresa) { props.updateCompany(profileRes.data.profile.empresa) }
                    } catch (_) {}
                    if (props.onSuccess) { props.onSuccess() }
                } else {
                    toastr.error(response.data.message || 'No pude crear la suscripción')
                }
            }
        } catch (error) {
            if (window.analytics) {
                window.analytics.track('Checkout fallido', {
                    planId: selectedPlan?._id || null,
                    errorCode: error?.response?.data?.code || null,
                    purchaseType
                })
            }
            toastr.error(error?.response?.data?.message || 'Error al procesar el pago')
        } finally {
            setLoading(false)
        }
    }

    const handleAlternativeSubmit = async (event) => {
        event.preventDefault()
        if (!selectedPlan) { return }

        // For active subscriptions, plan change still uses card flow
        if (alreadySubscribed) {
            const response = await ApiService.cambiarPlan({ planId: selectedPlan._id })
            if (response.data.success) {
                toastr.success(response.data.message)
                try {
                    const profileRes = await ApiService.getProfile()
                    if (profileRes.data?.profile?.empresa) { props.updateCompany(profileRes.data.profile.empresa) }
                } catch (_) {}
                if (props.onSuccess) { props.onSuccess() }
            } else {
                toastr.error(response.data.message || 'No pude cambiar el plan')
            }
            return
        }

        setLoading(true)
        try {
            const response = await ApiService.createSubscription({
                paymentMethod: paymentMethod, // 'spei' | 'oxxo'
                planId: selectedPlan._id
            })

            if (response.data.success) {
                setPendingInstructions({
                    type: paymentMethod,
                    ...response.data
                })
                pollUntilActive(() => {
                    setPendingInstructions(null)
                    ApiService.getProfile()
                        .then(r => { if (r.data?.profile?.empresa) { props.updateCompany(r.data.profile.empresa) } })
                        .catch(() => {})
                    toastr.success('¡Tu suscripción ha sido activada!')
                    if (props.onSuccess) { props.onSuccess() }
                })
            } else {
                toastr.error(response.data.message || 'No pude crear la suscripción')
            }
        } catch (error) {
            toastr.error(error?.response?.data?.message || 'Error al crear la suscripción')
        } finally {
            setLoading(false)
        }
    }

    if (pendingInstructions) {
        return (
            <div style={{ padding: '24px', background: '#f4f6f8', borderRadius: 8 }}>
                {pendingInstructions.type === 'oxxo' && (
                    <>
                        <h4>Pago en OXXO</h4>
                        <p>Presenta el siguiente voucher en cualquier tienda OXXO para activar tu suscripción.</p>
                        {pendingInstructions.voucherUrl && (
                            <a href={pendingInstructions.voucherUrl} target="_blank" rel="noreferrer" className="btn btn-warning">
                                Ver voucher OXXO
                            </a>
                        )}
                    </>
                )}
                {pendingInstructions.type === 'spei' && (
                    <>
                        <h4>Pago por SPEI</h4>
                        <p><strong>CLABE:</strong> {pendingInstructions.clabe}</p>
                        <p><strong>Banco:</strong> {pendingInstructions.banco}</p>
                        <p><strong>Monto:</strong> {pendingInstructions.monto}</p>
                    </>
                )}
                {polling && <p style={{ color: '#888', marginTop: 16 }}>Esperando confirmación de pago...</p>}
            </div>
        )
    }

    return (
        <Form onSubmit={paymentMethod === 'card' ? handleCardSubmit : handleAlternativeSubmit}>
            {plans && selectedPlan && (
                <>
                    {/* Plan selector */}
                    <Row>
                        <Col sm={12}>
                            <FormGroup>
                                <Label for="plan">Seleccionar Plan</Label>
                                <Input
                                    type="select"
                                    name="plan"
                                    id="plan"
                                    value={selectedPlan._id}
                                    onChange={(e) => {
                                        const found = plans.find(p => p._id === e.target.value)
                                        setSelectedPlan(found || selectedPlan)
                                    }}
                                >
                                    <option value="">Seleccionar un plan</option>
                                    {plans.filter(isPaidPlan).map((plan) => (
                                        <option key={plan._id} value={plan._id}>
                                            Timbrát {plan.nombre} — ${plan.precio} MXN / {plan.timbres} timbres
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Plan summary */}
                    <Row>
                        <Col sm={12}>
                            <Table size="sm" bordered>
                                <tbody>
                                    <tr>
                                        <td><strong>Timbrát {selectedPlan.nombre}</strong></td>
                                        <td>{selectedPlan.timbres} timbres/mes</td>
                                        <td><strong>$ {selectedPlan.precio} MXN</strong></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Col>
                    </Row>

                    {/* Downgrade pending alert */}
                    {props.empresa?.pendingPlanId && (
                        <Row>
                            <Col sm={12}>
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '12px 16px', marginBottom: 12, fontSize: 13 }}>
                                    Tienes un cambio de plan programado para tu próxima renovación. El nuevo plan incluirá menos timbres.
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Payment method selector (only for new subscriptions / credits) */}
                    {!alreadySubscribed && (
                        <Row style={{ marginBottom: 16 }}>
                            <Col sm={12}>
                                <Label>Método de pago</Label>
                                <Nav tabs>
                                    {PAYMENT_METHODS.map(pm => (
                                        <NavItem key={pm.id}>
                                            <NavLink
                                                active={paymentMethod === pm.id}
                                                onClick={() => setPaymentMethod(pm.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {pm.label}
                                            </NavLink>
                                        </NavItem>
                                    ))}
                                </Nav>
                                {purchaseType === 'subscription' && (
                                    <p style={{ fontSize: 12, color: '#888', marginTop: 6, marginBottom: 0 }}>
                                        OXXO y SPEI disponibles para compra de timbres sin suscripción.
                                    </p>
                                )}
                            </Col>
                        </Row>
                    )}

                    {/* Card form */}
                    {paymentMethod === 'card' && (
                        <>
                            {defaultPaymentMethod && (
                                <Row style={{ padding: '8px 16px' }}>
                                    <Col sm={12}>
                                        <FormGroup check>
                                            <Input
                                                type="checkbox"
                                                checked={useExistingPayment}
                                                onChange={() => setUseExistingPayment(!useExistingPayment)}
                                            />
                                            {' '}
                                            <Label check>
                                                <span style={{ marginLeft: 24 }}>
                                                    Usar {defaultPaymentMethod.card.brand} **** {defaultPaymentMethod.card.last4} (exp {defaultPaymentMethod.card.exp_month}/{defaultPaymentMethod.card.exp_year})
                                                </span>
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                            {!useExistingPayment && (
                                <Row style={{ padding: '8px 16px' }}>
                                    <Col sm={12}>
                                        <FormGroup>
                                            <Label>Tarjeta de Crédito o Débito</Label>
                                            <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: '10px 12px' }}>
                                                <CardElement />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}

                    {/* SPEI info */}
                    {paymentMethod === 'spei' && !alreadySubscribed && (
                        <Row style={{ padding: '8px 16px' }}>
                            <Col sm={12}>
                                <div style={{ background: '#e8f4fd', border: '1px solid #90CAF9', borderRadius: 6, padding: '12px 16px', fontSize: 13 }}>
                                    Al confirmar, recibirás los datos de la transferencia SPEI por correo. Tu suscripción se activará automáticamente cuando el SAT confirme el pago (puede tardar hasta 24 horas).
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* OXXO info */}
                    {paymentMethod === 'oxxo' && !alreadySubscribed && (
                        <Row style={{ padding: '8px 16px' }}>
                            <Col sm={12}>
                                <div style={{ background: '#fff8e1', border: '1px solid #FFB300', borderRadius: 6, padding: '12px 16px', fontSize: 13 }}>
                                    Al confirmar, recibirás un voucher para pagar en cualquier tienda OXXO. Tu suscripción se activará cuando se confirme el pago (máximo 72 horas).
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row className="form-actions" style={{ padding: '8px 0' }}>
                        <Col sm={12}>
                            <Button
                                type="submit"
                                color="success"
                                style={{ padding: '12px 32px' }}
                                disabled={loading || !selectedPlan || polling}
                            >
                                {loading || polling
                                    ? 'Procesando...'
                                    : alreadySubscribed
                                        ? 'Actualizar plan'
                                        : `Suscribirme — $${selectedPlan.precio} MXN/mes`
                                }
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </Form>
    )
}

const mapStatetoProps = (state) => {
    return { empresa: state.Auth.profile?.empresa || null }
}

const ConnectedPaymentForm = connect(
    mapStatetoProps, { updateCompany }
)(withRouter(PaymentForm))

const StripeWrapper = (props) => (
    <Elements stripe={stripePromise}>
        <ConnectedPaymentForm {...props} />
    </Elements>
)

export default StripeWrapper
